import React, { useState, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import {
  useFloating,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  FloatingOverlay,
  FloatingFocusManager,
  autoUpdate,
} from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';
import { createPopoverMiddleware, getArrowPosition } from '../../utils/floatingUiUtils';

export interface AnchorPosition {
  x: number;
  y: number;
}

type PopoverRole = 'dialog' | 'menu';

interface PopoverProps {
  children: React.ReactNode;
  content: React.ReactNode;
  placement?: Placement;
  preventCloseOnContentClick?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showArrow?: boolean;
  offset?: number | { mainAxis?: number; crossAxis?: number };
  /** When set, positions the popover at this viewport coordinate (e.g. for context menu). Hides arrow. */
  anchorPosition?: AnchorPosition | null;
  role?: PopoverRole;
  /** Additional classes appended to the floating element (for shadow/animation overrides). */
  className?: string;
  /** Overrides the trigger wrapper's default flex-grow behavior. */
  triggerClassName?: string;
}

const Popover = ({
  children,
  content,
  placement = 'bottom',
  className,
  triggerClassName,
  preventCloseOnContentClick = false,
  open,
  onOpenChange,
  showArrow = false,
  offset,
  anchorPosition = null,
  role: roleProp = 'dialog',
}: PopoverProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isVisible = isControlled ? open : internalOpen;

  const arrowRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  const virtualElement = useMemo(
    () =>
      anchorPosition
        ? {
            getBoundingClientRect: () => ({
              x: anchorPosition.x,
              y: anchorPosition.y,
              width: 0,
              height: 0,
              top: anchorPosition.y,
              left: anchorPosition.x,
              right: anchorPosition.x,
              bottom: anchorPosition.y,
            }),
            contextElement: document.body,
          }
        : null,
    [anchorPosition],
  );

  const showArrowForPosition = showArrow && !anchorPosition;

  const middleware = useMemo(
    () => createPopoverMiddleware(arrowRef, offset, showArrowForPosition),
    [offset, showArrowForPosition],
  );

  const { refs, floatingStyles, context, middlewareData, update } = useFloating({
    open: isVisible,
    onOpenChange: (value) => {
      if (isControlled) {
        onOpenChange?.(value);
      } else {
        setInternalOpen(value);
        onOpenChange?.(value);
      }
    },
    placement,
    middleware,
    whileElementsMounted: autoUpdate,
  });

  useLayoutEffect(() => {
    const useVirtual = isVisible && anchorPosition && virtualElement;
    if (useVirtual) {
      refs.setReference(virtualElement);
    } else if (triggerRef.current) {
      refs.setReference(triggerRef.current);
    }
    if (isVisible) {
      update();
    }
  }, [isVisible, anchorPosition, virtualElement, refs, update]);

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    outsidePress: true,
    escapeKey: true,
  });
  const role = useRole(context, { role: roleProp });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const setOpen = useCallback(
    (value: boolean) => {
      if (isControlled) {
        onOpenChange?.(value);
      } else {
        setInternalOpen(value);
        onOpenChange?.(value);
      }
    },
    [isControlled, onOpenChange],
  );

  const closePopover = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleContentClick = (event: React.MouseEvent) => {
    if (preventCloseOnContentClick) {
      event.stopPropagation();
    }
  };

  // Arrow key navigation between focusable children within the popover
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // For menu role, activate focused menuitem on Enter/Space
      if (roleProp === 'menu' && (event.key === 'Enter' || event.key === ' ')) {
        const target = document.activeElement as HTMLElement;
        // eslint-disable-next-line lingui/no-unlocalized-strings
        if (target && !target.matches('button, a, input, select, textarea')) {
          event.preventDefault();
          target.click();
          return;
        }
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      // Skip if a child already handled this (e.g. ReaderTagPopover input)
      if (event.defaultPrevented) return;

      const container = refs.floating.current;
      if (!container) return;

      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>(
          // eslint-disable-next-line lingui/no-unlocalized-strings
          'button:not([disabled]), a[href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])',
        ),
      );
      if (focusable.length === 0) return;

      event.preventDefault();

      const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
      let nextIndex: number;
      if (event.key === 'ArrowDown') {
        nextIndex = currentIndex < focusable.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : focusable.length - 1;
      }
      focusable[nextIndex]?.focus();
    },
    [refs.floating, roleProp],
  );

  // floating-ui's useRole/useInteractions put `aria-expanded`, `aria-haspopup`,
  // and `aria-controls` into getReferenceProps(). When those land on the
  // wrapping <span> (which has the generic role) axe flags every popover on
  // the page with aria-allowed-attr — and assistive tech misses them too,
  // because the *trigger* (the child <button>) is what should announce
  // "menu, collapsed". Split the props: route the ARIA pop-up signals onto
  // the trigger via cloneElement, keep the click handler / ID / ref on the
  // span so existing layout + open-on-click behaviour is unchanged.
  const referenceProps = getReferenceProps() as Record<string, unknown>;
  const {
    'aria-expanded': ariaExpanded,
    'aria-haspopup': ariaHaspopup,
    'aria-controls': ariaControls,
    ...spanReferenceProps
  } = referenceProps;

  const triggerWithAria = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        'aria-expanded': ariaExpanded,
        'aria-haspopup': ariaHaspopup,
        'aria-controls': ariaControls,
      })
    : children;

  return (
    <>
      <span
        ref={refs.setReference}
        className={`relative inline-block ${triggerClassName ?? 'flex-fluid'}`}
        data-popover-open={isVisible || undefined}
        {...spanReferenceProps}
      >
        {triggerWithAria}
      </span>
      {isVisible && (
        <FloatingPortal>
          {/* Overlay blocks all pointer events on background elements */}
          <FloatingOverlay
            lockScroll={false}
            className="z-overlay pointer-events-auto bg-transparent"
          />
          <FloatingFocusManager
            context={context}
            modal={roleProp === 'menu'}
            initialFocus={roleProp === 'menu' ? 0 : -1}
          >
            {/* Popover content as sibling to overlay */}
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className={`popover z-popover${className ? ` ${className}` : ''}`}
              {...getFloatingProps({
                onClick: handleContentClick,
                onKeyDown: handleKeyDown,
              })}
            >
              <div className="popover-content z-low relative py-2">
                {React.isValidElement(content) && content.props.closePopover
                  ? React.cloneElement(
                      content as React.ReactElement<{ closePopover?: () => void }>,
                      { closePopover },
                    )
                  : content}
              </div>
              {showArrowForPosition && middlewareData?.arrow && (
                <div
                  ref={arrowRef}
                  data-popper-arrow
                  style={{
                    position: 'absolute',
                    left:
                      middlewareData.arrow.x != null ? `${middlewareData.arrow.x}px` : '',
                    top:
                      middlewareData.arrow.y != null ? `${middlewareData.arrow.y}px` : '',
                    ...getArrowPosition(context.placement as Placement),
                    pointerEvents: 'none',
                    zIndex: -1,
                  }}
                >
                  <div
                    className="bg-surface-primary h-2 w-2 rotate-45"
                    style={{
                      borderStyle: 'solid',
                      borderColor: 'var(--border-default)',
                      borderWidth: context.placement?.startsWith('top')
                        ? '0 1px 1px 0'
                        : context.placement?.startsWith('bottom')
                          ? '1px 0 0 1px'
                          : context.placement?.startsWith('left')
                            ? '0 0 1px 1px'
                            : '1px 1px 0 0',
                    }}
                  />
                </div>
              )}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

export default Popover;
